import { nanoid } from 'nanoid';
import wretch from 'wretch';
import FormDataAddon from 'wretch/addons/formData';
import QueryStringAddon from 'wretch/addons/queryString';

import { isBrowser } from '../lib/is-browser';
import { logger } from '../lib/logger';
import { catchNetworkException, NetworkExceptionError } from './exceptions';

const wretchClient = wretch()
  .options({
    credentials: 'include',
  })
  .addon(QueryStringAddon)
  .addon(FormDataAddon)
  .catcherFallback((error) => {
    logger.error(error);

    throw catchNetworkException(error);
  })
  .resolve((_) =>
    _.fetchError((error) => {
      logger.error(error);

      throw catchNetworkException(error);
    }),
  );

export interface HTTPClientOptions {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  params?: any;
  headers?: HeadersInit;
  body?: BodyType<unknown>;
  responseType?: string;
  cookies?: Record<string, string>;
  isBlob?: boolean;
}

export const httpClient = async <Data>(url: string, options?: HTTPClientOptions) => {
  const { method = 'GET', params, body, headers = {}, cookies, isBlob = false } = options ?? {};

  const requestId = nanoid();
  const httpMethod = method.toLowerCase() as Lowercase<typeof method>;

  // Add cookies to headers
  const finalHeaders: HeadersInit = { ...headers };
  if (cookies && Object.keys(cookies).length > 0) {
    const cookieString = Object.entries(cookies)
      .map(([key, value]) => `${key}=${value}`)
      .join('; ');
    if (cookieString) {
      (finalHeaders as Record<string, string>)['Cookie'] = cookieString;
    }
  }

  /**
   * Hide sensitive data in browser console
   */
  if (isBrowser()) {
    logger.debug(`Network request: ${requestId} • ${method} • ${url} • %s`, JSON.stringify(options, null, 2));
  } else {
    logger.info(`Network request: ${requestId} • ${method} • ${url} • %s`, JSON.stringify(options, null, 2));
  }

  const timeStart = performance.now();
  try {
    const response = await wretchClient
      .url(url, true)
      .headers(finalHeaders)
      .query(params)
      .body(body)
      [httpMethod]()
      [isBlob ? 'blob' : 'json']<Data>();

    return response;
  } catch (error) {
    // Логируем детали ошибки для отладки
    if (error instanceof Error && 'response' in error) {
      const response = (error as any).response;
      logger.error(`Request failed: ${method} ${url}`);
      logger.error(`Status: ${response?.status}`);
      logger.error(`Response:`, response?.data);
    }

    throw error;
  } finally {
    const timeEnd = performance.now();

    logger.info(`Network request: ${requestId} took ${timeEnd - timeStart} milliseconds.`);
  }
};

export type ErrorType<Error> = NetworkExceptionError<Error>;

export type BodyType<BodyData> = BodyData;
